import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import type { AdminUser, CreateAdminUserRequest, UpdateAdminUserRequest } from '@/types/admin';

const KEYS = {
  all: ['admin-users'] as const,
  list: (scope?: string) => [...KEYS.all, 'list', scope ?? 'admin'] as const,
};

function extractResults(data: { results: AdminUser[] } | AdminUser[]): AdminUser[] {
  return Array.isArray(data) ? data : data.results;
}

export function useAdminUsers() {
  return useQuery({
    queryKey: KEYS.list('admin'),
    queryFn: async () => {
      const { data } = await api.get<{ results: AdminUser[] } | AdminUser[]>(API_ROUTES.adminUsers);
      return extractResults(data);
    },
  });
}

export function usePendingUsers() {
  return useQuery({
    queryKey: KEYS.list('pending'),
    queryFn: async () => {
      const { data } = await api.get<{ results: AdminUser[] } | AdminUser[]>(
        API_ROUTES.adminUsers,
        { params: { scope: 'pending' } },
      );
      return extractResults(data);
    },
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userData: CreateAdminUserRequest) => {
      const { data } = await api.post<AdminUser>(API_ROUTES.adminUsers, userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...userData }: UpdateAdminUserRequest & { id: number }) => {
      const { data } = await api.patch<AdminUser>(`${API_ROUTES.adminUsers}${id}/`, userData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`${API_ROUTES.adminUsers}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export function useApproveUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<AdminUser>(`${API_ROUTES.adminUsers}${id}/approve/`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
}

export async function checkEmailExists(email: string): Promise<boolean> {
  const { data } = await api.get<{ exists: boolean }>(API_ROUTES.adminCheckEmail, {
    params: { email },
  });
  return data.exists;
}
